import {
    MindyLogoDark,
    MindyLogoLight,
    QuadmindsAILogo,
    Separator,
    SidebarTrigger,
    useTheme,
    useIsMobile,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    Avatar,
    AvatarImage,
    AvatarFallback,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuGroup,
    useToast,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from '@quadminds/mindkit';

import { MindyDocRoles, MindyRolesPermissions, MindyUsersPermissions } from '@quadminds/mindy-shared';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { clearTokensFromStorage, isProduction } from '@quadminds/shared/utils';
import { RiCircleLine, RiRadioButtonLine } from '@remixicon/react';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, HelpCircle, LogOut, Moon, Settings2, Sun, Video } from 'lucide-react';
import React, { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import PlatformSwitcher from './PlatformSwitcher';
import LogoClaro from '../../../assets/img/logo_claro.svg';
import { getUsersLandingUrl } from '../../../config';
import { useIsKnowledge } from '../../../hooks/useIsKnowledge';
import { useSelectOrganization } from '../../../hooks/useSelectOrganization';
import { UserLegacy } from '../../../mocks/fixtures/organization.faker';
import { agentsApi } from '../../../services/agents-api.service';
import { getAuthService } from '../../../services/auth.service';
import { rememberAccessToken } from '../../../utils/agentsRequest';
import { shouldHideSuggestedQuestions } from '../../../utils/utils';
import { KnowledgeVideoModal } from '../../Onboarding/KnowledgeVideoModal';

// eslint-disable-next-line @typescript-eslint/ban-types
const Header = forwardRef<HTMLElement, {}>((props, ref) => {
    const { theme, setTheme } = useTheme();
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const users: UserLegacy[] | undefined = [];
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const [currentOrganization, setCurrentOrganization] = React.useState<string | undefined>(
        userInfo?.currentOrganization,
    );

    const [value, setValue] = React.useState('');
    const [isKnowledgeVideoOpen, setIsKnowledgeVideoOpen] = React.useState(false);
    const { mutateAsync, isPending } = useSelectOrganization();
    const { toast } = useToast();

    const handleLogout = () => {
        clearTokensFromStorage();
        localStorage.removeItem('isStorkUser');
        getAuthService()
            .logout()
            .then(() => {
                globalThis.location.assign(
                    `${getUsersLandingUrl()}/login?logout=true&redirect=${globalThis.location.href}`,
                );
            });
    };

    const userCurrentOrganization = userInfo?.organizations?.find(
        (organization) => organization.id === userInfo?.currentOrganization,
    );
    const orgPhoto = userCurrentOrganization?.photo;
    const orgName = userCurrentOrganization?.name
        ?.split(' ')
        .slice(0, 2)
        .map((name) => name.charAt(0).toUpperCase())
        .join('');

    const userName = `${userInfo?.firstName} ${userInfo?.lastName}`
        ?.split(' ')
        .slice(0, 2)
        .map((name) => name.charAt(0).toUpperCase())
        .join('');

    const handleChangeOrganization = async (organizationId: string) => {
        if (organizationId === currentOrganization) return;

        setCurrentOrganization(organizationId);

        try {
            const { accessToken } = await mutateAsync(organizationId);
            rememberAccessToken(accessToken);

            // Derive basename dynamically from the difference between the full
            // browser path and React Router's path (which excludes the basename).
            const appBasename = globalThis.location.pathname.endsWith(routerLocation.pathname)
                ? globalThis.location.pathname.slice(
                      0,
                      globalThis.location.pathname.length - routerLocation.pathname.length,
                  )
                : '';

            const topLevelSegment = routerLocation.pathname.split('/').filter(Boolean)[0];
            const topLevelPath = topLevelSegment ? `/${topLevelSegment}` : '/';

            // replace() avoids adding a spurious browser history entry.
            globalThis.location.replace(appBasename + topLevelPath);
        } catch {
            setCurrentOrganization(userInfo?.currentOrganization);
            toast({
                title: t('header.organization.changeOrganizationError'),
                description: t('header.organization.changeOrganizationErrorDescription'),
                variant: 'destructive',
            });
        }
    };

    const hasAccessToSettings =
        userInfo?.permissions.includes(MindyUsersPermissions.ReadUser) ||
        userInfo?.permissions.includes(MindyRolesPermissions.ReadRole) ||
        userInfo?.roles.includes(MindyDocRoles.ADMIN_DOC);

    const { isKnowledge } = useIsKnowledge();
    const hideItems = shouldHideSuggestedQuestions(userInfo);

    useEffect(() => {
        queryClient.prefetchQuery({
            queryKey: ['questions'],
            queryFn: () => agentsApi.getAllQuestions(),
        });
    }, [queryClient]);

    return (
        <header
            ref={ref}
            className="flex fixed top-0 items-center bg-background z-10 justify-between space-x-2 py-2 px-4 md:px-8 border-b w-full"
        >
            <div className="flex items-center space-x-3 flex-shrink-0">
                {isMobile && (
                    <>
                        <SidebarTrigger />
                        <Separator orientation="vertical" className="h-4" />
                    </>
                )}
                <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="h-8 p-0 border-none bg-transparent cursor-pointer"
                    aria-label="Ir a inicio"
                >
                    <img src={theme === 'dark' ? MindyLogoLight : MindyLogoDark} alt="Mindy" className="h-8" />
                </button>
                <Separator orientation="vertical" className="h-4 max-sm:hidden" />
                <div className="h-4 md:h-6 p-0 border-none bg-transparent cursor-pointer max-sm:hidden">
                    <img
                        src={isKnowledge ? LogoClaro : QuadmindsAILogo}
                        alt={isKnowledge ? 'Claro' : 'QuadmindsAI'}
                        className={`h-4 md:h-6 ${isKnowledge ? '!-mt-0.5' : ''}   `}
                    />
                </div>
            </div>
            <div className="flex items-center space-x-2 ml-auto">
                <PlatformSwitcher />
                <DropdownMenu>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild data-testid="user-menu-trigger">
                                <div className="h-10 flex gap-1 cursor-pointer items-center">
                                    <Avatar className="h-8 w-8 !rounded overflow-hidden">
                                        <AvatarImage
                                            className="cursor-pointer !rounded object-cover"
                                            src={orgPhoto}
                                            alt="User Avatar"
                                        />
                                        <AvatarFallback className="!rounded">{orgName}</AvatarFallback>
                                    </Avatar>
                                    <Avatar className="h-8 w-8">
                                        <AvatarFallback className="bg-gray-200 dark:bg-gray-700">
                                            {userName}
                                        </AvatarFallback>
                                    </Avatar>
                                </div>
                            </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="end">
                            <p className="font-roboto text-sm font-medium">{`${userInfo?.firstName || ''} ${
                                userInfo?.lastName || ''
                            }`}</p>
                            <p className="font-roboto text-xs font-normal text-muted-foreground">
                                {userCurrentOrganization?.name}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="w-[360px]">
                        <DropdownMenuGroup className="w-full pt-[22px] p-4 flex flex-col items-center justify-center">
                            <Avatar className="h-16 w-16 mb-2.5">
                                <AvatarFallback className="!rounded">{userName}</AvatarFallback>
                            </Avatar>
                            {(userInfo?.firstName || userInfo?.lastName) && (
                                <p className="font-poppins font-normal dark:text-white text-base">
                                    {[userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ')}
                                </p>
                            )}
                            {userInfo?.email && (
                                <p className="font-roboto font-normal text-[#00000099] dark:text-[#FFFFFF99] text-xs">
                                    {userInfo.email}
                                </p>
                            )}
                        </DropdownMenuGroup>
                        <DropdownMenuGroup className="w-full pb-4 flex flex-col items-center justify-center px-1 cursor-pointer">
                            {userInfo?.organizations.map((organization, index, array) => {
                                if (userInfo.organizations.length === 1) return null;
                                const currentName = organization.name
                                    ?.split(' ')
                                    .slice(0, 2)
                                    .map((name) => name.charAt(0).toUpperCase())
                                    .join('');
                                return (
                                    <div
                                        id={`organization-${organization.id}`}
                                        key={organization.id}
                                        className={twMerge(
                                            'flex items-center w-full box-border h-14 bg-gray-100 dark:bg-[#37474F] border-b border-gray-200 dark:border-[#455A64] p-3 hover:bg-gray-200 dark:hover:bg-[#455A64]',
                                            index === 0 && 'rounded-t-md',
                                            index === array.length - 1 && 'rounded-b-md border-none',
                                            isPending && 'animate-pulse',
                                        )}
                                    >
                                        <Avatar className="h-8 w-8 !rounded mr-2.5">
                                            <AvatarImage
                                                className="cursor-pointer !rounded object-cover"
                                                src={organization?.photo}
                                                alt="User Avatar"
                                            />
                                            <AvatarFallback className="!rounded">{currentName}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="text-xs font-semibold font-poppins dark:text-white">
                                                {organization.name}
                                            </p>
                                            <p className="font-roboto text-[12px] leading-[14px] font-normal text-[#00000099] dark:text-[#FFFFFF99]">
                                                {organization.email}
                                            </p>
                                        </div>
                                        <button
                                            className="ml-auto cursor-pointer"
                                            onClick={() => handleChangeOrganization(organization.id)}
                                            disabled={isPending}
                                        >
                                            {currentOrganization === organization.id ? (
                                                <RiRadioButtonLine className="text-primary" />
                                            ) : (
                                                <RiCircleLine className="text-muted-foreground" />
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </DropdownMenuGroup>
                        {!isProduction() && users !== undefined && users.length > 0 && (
                            <>
                                <DropdownMenuLabel>{t('header.userMenu.selectUser')}</DropdownMenuLabel>
                                <div className="px-2 mb-2">
                                    <select
                                        value={value}
                                        onChange={(e) => setValue(e.target.value)}
                                        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                        aria-label={t('header.userMenu.selectUser')}
                                    >
                                        <option value="">{t('header.userMenu.selectUser')}</option>
                                        {users.map((user) => (
                                            <option key={user.legacyUserId} value={user.legacyUserId}>
                                                {user.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <DropdownMenuItem
                                className="py-2 text-base group"
                                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                                data-testid="theme-switch-button"
                            >
                                {theme === 'dark' ? (
                                    <Sun className="text-lg text-foreground/70 group-hover:text-gray-100 group-focus:text-gray-100" />
                                ) : (
                                    <Moon className="text-lg text-foreground/70 group-hover:text-gray-100 group-focus:text-gray-100" />
                                )}
                                <span>
                                    {theme === 'dark' ? t('header.userMenu.lightMode') : t('header.userMenu.darkMode')}
                                </span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {!hideItems && !isKnowledge && (
                                <>
                                    <DropdownMenuItem
                                        className="py-2 text-base group"
                                        onClick={() => navigate('/suggested-questions')}
                                        data-testid="questions-button"
                                    >
                                        <HelpCircle className="text-lg text-foreground/70 group-hover:text-gray-100 group-focus:text-gray-100" />
                                        <span>Guía de Preguntas</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            {hasAccessToSettings && (
                                <DropdownMenuItem
                                    className="py-2 text-base group"
                                    onClick={() => {
                                        navigate('/settings/');
                                    }}
                                    data-testid="settings-button"
                                >
                                    <Settings2 className="text-lg text-foreground/70 group-hover:text-gray-100 group-focus:text-gray-100" />
                                    <span>{t('header.userMenu.settings')}</span>
                                </DropdownMenuItem>
                            )}
                            {hasAccessToSettings && !isKnowledge && <DropdownMenuSeparator />}
                            {isKnowledge && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem asChild className="py-2 text-base cursor-pointer group">
                                        <a
                                            href="https://www.quadminds.com/descargas/manual-de-usuario-mindy-conocimiento/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FileText className="text-lg text-foreground/70 group-hover:text-gray-100 group-focus:text-gray-100" />
                                            <span>{t('header.userMenu.knowledgeManual')}</span>
                                        </a>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="py-2 text-base group"
                                        onClick={() => setIsKnowledgeVideoOpen(true)}
                                    >
                                        <Video className="text-lg text-foreground/70 group-hover:text-gray-100 group-focus:text-gray-100" />
                                        <span>{t('header.userMenu.knowledgeVideo')}</span>
                                    </DropdownMenuItem>
                                </>
                            )}
                            {isKnowledge && <DropdownMenuSeparator />}
                            <DropdownMenuItem onClick={() => handleLogout()} className="py-2 text-base group">
                                <LogOut className="text-lg text-foreground/70 group-hover:text-gray-100 group-focus:text-gray-100" />
                                <span>{t('header.userMenu.logout')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuGroup>
                                {!isKnowledge && (
                                    <DropdownMenuItem
                                        asChild
                                        className="py-2 text-sm cursor-pointer text-muted-foreground"
                                    >
                                        <a
                                            href="https://docs.quadminds.com/docs/mindy-el-asistente-de-ia-de-quadminds"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {t('header.userMenu.userManual')}
                                        </a>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem asChild className="py-2 text-sm cursor-pointer text-muted-foreground">
                                    <a
                                        href={
                                            isKnowledge
                                                ? 'https://www.quadminds.com/terminos-y-condiciones-mindy-conocimiento'
                                                : 'https://www.quadminds.com/terminos-y-condiciones-de-uso-mindy'
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="terms-link"
                                    >
                                        {t('onboarding.terms.openModalText')}
                                    </a>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className="py-2 text-sm cursor-pointer text-muted-foreground">
                                    <a
                                        href="https://www.quadminds.com/politica-de-privacidad-mindy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="privacy-link"
                                    >
                                        {t('onboarding.terms.privPolitics')}
                                    </a>
                                </DropdownMenuItem>
                            </DropdownMenuGroup>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
                <KnowledgeVideoModal isOpen={isKnowledgeVideoOpen} onOpenChange={setIsKnowledgeVideoOpen} />
            </div>
        </header>
    );
});

export default Header;
